import { EFinancingProgram, EWorksheetDecision } from '@if/core/types'
import { Button, Grid, Paper, Stack, Typography } from '@mui/material'
import { Breadcrumb, PageError, PageSpinner } from '@src/components'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useUpdateEasyCareWorksheetDecision } from '@src/data/api/easycare-worksheet/easycare-worksheet-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { useBusinessErrors } from '@src/data/selectors/app-config-selectors'
import {
  useAreAllTasksRequiredForCVTCompleted,
  useConfirmAllIncomesStatus,
  useIsFinalDecisionPending,
} from '@src/data/selectors/credit-selectors'
import { useAreAllDocumentsRequiredForCVT } from '@src/data/selectors/document-selectors'
import { BaseWorksheetFundingThresholdExtend, WorksheetDecision } from '@src/data/types'
import { EasyCareWorksheet, EasyCareWorksheetDecisionDTO } from '@src/data/types/EasyCareWorksheetSchema'
import { useUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import WorksheetDecisionDialog from '../components/LoanSummary/WorksheetDecisionDialog'
import WorksheetDecisionSection from '../components/LoanSummary/WorksheetDecisionSection'
import { useDialogDecisionDefaultValue } from '../viewWorksheet-hooks'

const EditDecisionDialogOpen = 'EditDecisionDialogOpen'

const EasyCareWorksheetViewPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { creditApplication, worksheet: contextWorksheet, isLoading, documents, requiredDocuments } =
    useCreditApplicationData()
  const worksheet = contextWorksheet as EasyCareWorksheet | undefined
  const error = useBusinessErrors()
  const user = useUser()

  const [updateDecision] = useUpdateEasyCareWorksheetDecision()
  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()
  const [currentOpenDialog, setCurrentOpenDialog] = useState<string>('')

  const finalDecisionIsPending = useIsFinalDecisionPending()
  const areAllTasksRequiredForCVTCompleted = useAreAllTasksRequiredForCVTCompleted()
  const areAllDocumentsRequiredForCVTApproved = useAreAllDocumentsRequiredForCVT(documents, requiredDocuments)
  const confirmAllIncomesStatus = useConfirmAllIncomesStatus()
  const editDisabled = creditApplication?.editLocked === true || finalDecisionIsPending

  const dialogDecisionDefaultValue = useDialogDecisionDefaultValue(
    creditApplication!,
    worksheet!,
    areAllTasksRequiredForCVTCompleted,
    areAllDocumentsRequiredForCVTApproved,
    confirmAllIncomesStatus!,
  )

  const closeDialogs = useCallback(() => {
    setCurrentOpenDialog('')
  }, [])

  const handleSaveDecision = useCallback(
    async (data: WorksheetDecision) => {
      if (!worksheet || !creditApplication) return

      const updatedFinalDecision: EasyCareWorksheetDecisionDTO = {
        creditApplicationId: worksheet.creditApplicationId,
        decision: data.decision,
        otherReason: data.otherReason,
        versionTag: worksheet.versionTag,
      } as EasyCareWorksheetDecisionDTO

      if (data.decision === EWorksheetDecision.ApprovedWithCondition) {
        const fundingThresholdExtend: BaseWorksheetFundingThresholdExtend = {
          maxPmtAmount: data.maxPmtAmount,
          maxTotalAmountFinanced: data.maxTotalAmountFinanced,
          maxTermDuration: data.maxTermDuration,
        }
        updatedFinalDecision.fundingThresholdExtend = fundingThresholdExtend
      }

      try {
        await updateDecision(updatedFinalDecision)
        closeDialogs()
        await invalidateFullCreditApplication(creditApplication.id, creditApplication.financingProgramId)
      } catch (err) {
        reportErrorToConsole(err)
      }
    },
    [worksheet, creditApplication, closeDialogs, invalidateFullCreditApplication, updateDecision],
  )

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.EasyCareProtectionPlan}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  const rows = useMemo<{ label: string; value: string | number }[]>(
    () =>
      worksheet
        ? [
            {
              label: t('easyCare.vehicleAssistance'),
              value: t(worksheet.includeVehicleAssistance ? 'common.yes' : 'common.no'),
            },
            {
              label: t('easyCare.loanProtection'),
              value: t(worksheet.includeLoanProtection ? 'common.yes' : 'common.no'),
            },
            {
              label: t('easyCare.vehicleAssistanceTerm'),
              value: worksheet.vehicleAssistanceTerm
                ? t('easyCare.months', { count: worksheet.vehicleAssistanceTerm })
                : '—',
            },
            { label: t('worksheetCommon.amountRequested'), value: worksheet.amountRequested },
            { label: t('easyCare.loanTerm'), value: t('easyCare.months', { count: worksheet.term }) },
            {
              label: t('worksheet.paymentFrequency'),
              value: t(`EPaymentFrequency.${worksheet.paymentFrequency}`, { ns: 'enum' }),
            },
            { label: t('worksheetCommon.activationDate'), value: worksheet.deliveryOn },
            { label: t('worksheet.firstPaymentOn'), value: worksheet.firstPaymentOn },
            { label: t('worksheetCommon.paymentAmount'), value: worksheet.paymentAmountForFrequency },
            { label: t('worksheet.totalAmountFinanced'), value: worksheet.totalAmountFinanced },
          ]
        : [],
    [t, worksheet],
  )

  return (
    <div>
      <PageSpinner isLoading={isLoading} />
      {!isLoading && creditApplication && worksheet && (
        <Stack spacing={3}>
          <PageError errors={error} />
          <Stack
            direction={{ sm: 'row', xs: 'column' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ sm: 'self-end', xs: 'self-start' }}
          >
            <Breadcrumb trees={breadCrumbs} />
            <Button
              fullWidth={false}
              variant="contained"
              onClick={() =>
                navigate(`/Applications/${EFinancingProgram.EasyCareProtectionPlan}/${creditApplication.id}/worksheet/edit`)
              }
            >
              {t('common.edit')}
            </Button>
          </Stack>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {rows.map((row) => (
                <Grid key={row.label} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    {row.label}
                  </Typography>
                  <Typography variant="body1">{row.value}</Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
          <WorksheetDecisionSection
            worksheet={worksheet}
            onClick={() => setCurrentOpenDialog(EditDecisionDialogOpen)}
            editDisabled={editDisabled}
            userCanEditWorksheet={user?.rights.canEditWorksheet === true}
          />
          <WorksheetDecisionDialog
            open={currentOpenDialog === EditDecisionDialogOpen}
            title={t('worksheet.decision')}
            onCancel={closeDialogs}
            onConfirm={handleSaveDecision}
            worksheetWarnings={null}
            defaultValue={dialogDecisionDefaultValue}
            worksheet={worksheet}
            creditapplication={creditApplication}
          />
        </Stack>
      )}
    </div>
  )
}

export default EasyCareWorksheetViewPage
